import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import {
  Upload, Trash2, Loader2, Save, Pencil, X, GripVertical,
  Image as ImageIcon, Search, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getImageUrl = (img) => {
  if (img.storage_path) return `${API}/gallery/image/${img.id}`;
  if (img.image_url) return img.image_url;
  return '';
};

// ============= SORTABLE GALLERY ITEM =============
const SortableGalleryItem = ({ image, index, onEdit, onDelete }) => {
  const { ref } = useSortable({ id: image.id, index, type: 'gallery-image', accept: 'gallery-image', group: 'gallery' });
  const url = getImageUrl(image);

  return (
    <div ref={ref} className="relative group bg-white rounded-xl border border-[#E7E5E4] overflow-hidden" data-testid={`gallery-item-${image.id}`}>
      <div className="aspect-square relative">
        <img src={url} alt={image.alt_text || image.original_filename} className="w-full h-full object-cover" loading="lazy" />
        {/* Order badge */}
        <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{index + 1}</span>
        {/* Drag handle */}
        <div className="absolute top-2 right-2 cursor-grab active:cursor-grabbing touch-none p-1.5 bg-white/80 backdrop-blur rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical className="h-4 w-4 text-[#57534E]" />
        </div>
        {/* Hover actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button onClick={() => onEdit(image)} className="p-2 bg-white rounded-full shadow-md hover:bg-[#F5F3EF]" data-testid={`gallery-edit-${image.id}`}>
            <Pencil className="h-4 w-4 text-[#1C1917]" />
          </button>
          <button onClick={() => onDelete(image)} className="p-2 bg-white rounded-full shadow-md hover:bg-red-50" data-testid={`gallery-delete-${image.id}`}>
            <Trash2 className="h-4 w-4 text-red-600" />
          </button>
        </div>
      </div>
      {/* Info */}
      <div className="p-2.5">
        <p className="text-sm font-medium text-[#1C1917] truncate">{image.title || image.original_filename}</p>
        {image.description && <p className="text-xs text-[#78716C] truncate mt-0.5">{image.description}</p>}
      </div>
    </div>
  );
};

// ============= EDIT MODAL =============
const EditModal = ({ image, open, onClose, onSave, saving }) => {
  const [form, setForm] = useState({ title: '', description: '', alt_text: '' });

  useEffect(() => {
    if (image) {
      setForm({ title: image.title || '', description: image.description || '', alt_text: image.alt_text || '' });
    }
  }, [image]);

  if (!image) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Editar imagen</DialogTitle></DialogHeader>
        <div className="flex gap-4 mb-4">
          <img src={getImageUrl(image)} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
          <div className="text-sm text-[#78716C]">
            <p>{image.original_filename}</p>
            <p className="text-xs mt-1">Orden: #{image.display_order + 1}</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-1">Titulo</label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Titulo de la imagen" data-testid="gallery-edit-title" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-1">Descripcion</label>
            <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={2} placeholder="Descripcion opcional" data-testid="gallery-edit-description" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-1">Texto alternativo (alt)</label>
            <Input value={form.alt_text} onChange={e => setForm(p => ({ ...p, alt_text: e.target.value }))} placeholder="Descripcion para accesibilidad" data-testid="gallery-edit-alt" />
          </div>
        </div>
        <div className="flex gap-3 pt-4 border-t border-[#E7E5E4] mt-4">
          <Button onClick={() => onSave(image.id, form)} disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="gallery-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Guardar
          </Button>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Cancelar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============= MAIN ADMIN GALLERY =============
const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingImage, setEditingImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const fileInputRef = useRef(null);
  const snapshotRef = useRef(null);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/gallery`, { headers });
      setImages(res.data);
      setOrderChanged(false);
    } catch { toast.error('Error cargando galeria'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  // Upload
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);
    let uploaded = 0;

    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        await axios.post(`${API}/admin/gallery/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
        setUploadProgress(Math.round((uploaded / files.length) * 100));
      } catch (err) {
        toast.error(`${file.name}: ${err.response?.data?.detail || 'Error'}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} imagen(es) subida(s)`);
      fetchImages();
    }
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Edit
  const handleSaveEdit = async (imageId, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/gallery/${imageId}`, data, { headers });
      toast.success('Imagen actualizada');
      setEditingImage(null);
      fetchImages();
    } catch { toast.error('Error actualizando'); }
    finally { setSaving(false); }
  };

  // Delete
  const handleDelete = async (image) => {
    const name = image.title || image.original_filename;
    if (!window.confirm(`¿Eliminar "${name}"? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${image.id}`, { headers });
      toast.success('Imagen eliminada');
      setImages(prev => prev.filter(i => i.id !== image.id));
    } catch { toast.error('Error eliminando'); }
  };

  // Save order
  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const order = images.map((img, i) => ({ id: img.id, display_order: i }));
      await axios.put(`${API}/admin/gallery/reorder`, { order }, { headers });
      toast.success('Orden guardado');
      setOrderChanged(false);
    } catch { toast.error('Error guardando orden'); }
    finally { setSavingOrder(false); }
  };

  // Seed
  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/admin/gallery/seed`, {}, { headers });
      toast.success(res.data.message);
      fetchImages();
    } catch { toast.error('Error migrando'); }
  };

  // DnD handlers
  const handleDragStart = () => { snapshotRef.current = [...images]; };
  const handleDragEnd = (event) => {
    if (event.canceled) { setImages(snapshotRef.current); return; }
    const { source } = event.operation;
    if (source && typeof source.initialIndex === 'number' && typeof source.index === 'number') {
      const from = source.initialIndex;
      const to = source.index;
      if (from !== to) {
        setImages(prev => {
          const arr = [...prev];
          const [moved] = arr.splice(from, 1);
          arr.splice(to, 0, moved);
          return arr;
        });
        setOrderChanged(true);
      }
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-gallery-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917]">Galeria</h1>
          <p className="text-[#78716C] text-sm mt-1">{images.length} imagen(es) — Arrastra para reordenar</p>
        </div>
        <div className="flex gap-3">
          {orderChanged && (
            <Button onClick={handleSaveOrder} disabled={savingOrder} className="bg-green-600 hover:bg-green-700" data-testid="gallery-save-order-btn">
              {savingOrder ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Guardar orden
            </Button>
          )}
          {images.length === 0 && !loading && (
            <Button variant="outline" onClick={handleSeed} data-testid="gallery-seed-btn">Migrar imagenes iniciales</Button>
          )}
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleUpload} className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="gallery-upload-btn">
            {uploading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Subiendo {uploadProgress}%</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Subir imagenes</>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" /></div>
      ) : images.length === 0 ? (
        <div className="text-center py-20 text-[#78716C]">
          <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No hay imagenes en la galeria</p>
          <p className="text-sm mt-1">Sube fotografias o migra las imagenes actuales</p>
        </div>
      ) : (
        <DragDropProvider onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {images.map((image, index) => (
              <SortableGalleryItem
                key={image.id}
                image={image}
                index={index}
                onEdit={setEditingImage}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </DragDropProvider>
      )}

      {/* Edit Modal */}
      <EditModal
        image={editingImage}
        open={!!editingImage}
        onClose={() => setEditingImage(null)}
        onSave={handleSaveEdit}
        saving={saving}
      />
    </div>
  );
};

export default AdminGallery;
